import { useAuth, useUser } from "@clerk/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useSearch } from "@tanstack/react-router"
import { LoadingSpinner } from "@/components/global/elements/loading-spinner"
import { PageHeader } from "@/components/layout"
import { ManageExpansionGroupForm } from "@/components/manage-content/manage-expansion-group-form"
import { useOrpc } from "@/integrations/orpc"
import { useProductionEntity } from "@/queries/production/use-production-entity"
import {
	detectExpansionGroupFieldDifferences,
	transformProductionToExpansionGroupFormData,
	transformStagingToExpansionGroupFormData,
} from "@/utils/form.helpers"

export const Route = createFileRoute("/expansion-groups/$id/manage")({
	component: ManageExpansionGroupPage,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			gameSlug: search.gameSlug as string | undefined,
		}
	},
})

function ManageExpansionGroupPage() {
	const { id } = Route.useParams()
	const searchParams = useSearch({ from: "/expansion-groups/$id/manage" })
	const { user } = useUser()
	const userEmail =
		user?.primaryEmailAddress?.emailAddress ??
		user?.emailAddresses?.[0]?.emailAddress ??
		"unknown@cardnexus.com"
	const { client } = useOrpc()
	const { isSignedIn } = useAuth()

	const gameSlug = searchParams.gameSlug || ""
	const groupSlug = id

	const {
		data: stagingData,
		isLoading: stagingLoading,
		error: stagingError,
	} = useQuery({
		queryKey: [
			"gameAdmin",
			"staging",
			"getEntity",
			"expansionGroup",
			gameSlug,
			groupSlug,
		],
		queryFn: async () => {
			const result = await client.staging.getEntity({
				entityType: "expansionGroup",
				gameSlug: gameSlug,
				entitySlug: groupSlug,
			})

			if (result.entityType !== "expansionGroup") {
				throw new Error(
					`Expected expansionGroup entity, got ${result.entityType}`,
				)
			}

			return result
		},
		enabled: isSignedIn && !!gameSlug && !!groupSlug,
	})

	const { data: productionData, isLoading: productionLoading } =
		useProductionEntity({
			gameSlug,
			entitySlug: groupSlug,
			entityType: "expansionGroup",
			enabled: isSignedIn && !!gameSlug && !!groupSlug,
		})

	const stagingFormData = transformStagingToExpansionGroupFormData(stagingData)
	const productionFormData =
		transformProductionToExpansionGroupFormData(productionData)

	const fieldDifferences = detectExpansionGroupFieldDifferences(
		productionFormData,
		stagingFormData,
	)

	const isLoading = stagingLoading || productionLoading

	if (isLoading) {
		return (
			<div className="flex justify-center p-6">
				<LoadingSpinner />
			</div>
		)
	}

	if (stagingError || !stagingFormData) {
		return (
			<div className="p-6">
				<PageHeader
					title="Expansion group not found"
					description={
						stagingError
							? stagingError.message
							: `The expansion group with ID ${id} could not be found.`
					}
				/>
			</div>
		)
	}

	return (
		<ManageExpansionGroupForm
			productionData={productionFormData}
			stagingData={stagingFormData}
			fieldDifferences={fieldDifferences}
			gameSlug={gameSlug}
			groupSlug={groupSlug}
			userEmail={userEmail}
		/>
	)
}
